// Les nouvelles de l'API en direct (`GET /api/evenements`, app/evenements.py), par une seule connexion partagée : chaque
// composant qui écoute s'abonne, la connexion s'ouvre au premier et se ferme après le dernier.
//
// Une connexion coupée (l'API redémarre, le Mac sort de veille, une file qui déborde côté serveur) a pu perdre des
// nouvelles : à son retour, chaque abonné est prévenu (`reprise`) pour tout relire.

import { EVENEMENTS, type Evenement } from "./api";

// Les nouvelles que l'espace d'un projet attend en plus de celles de l'API (lib/api) : un fichier du dossier écrit,
// importé, rangé ou supprimé, par l'entreprise ou par l'agent.
export type Nouvelle = Evenement | { type: "dossier"; affaire: string };

type Abonne = { nouvelle: (n: Nouvelle) => void; reprise?: () => void };

const abonnes = new Set<Abonne>();
let source: EventSource | null = null;
let coupee = false; // la connexion a été perdue depuis la dernière ouverture
let relance: number | undefined;

const RELANCE = 3000; // ms avant de rouvrir une connexion que le navigateur a abandonnée

function ouvrir() {
  if (source || typeof window === "undefined") return;
  const s = new EventSource(EVENEMENTS);
  source = s;
  s.onopen = () => {
    if (!coupee) return;
    coupee = false;
    for (const a of [...abonnes]) a.reprise?.();
  };
  s.onmessage = (m) => {
    let n: Nouvelle;
    try {
      n = JSON.parse(m.data) as Nouvelle;
    } catch {
      return; // une ligne illisible ne coupe pas le flux
    }
    for (const a of [...abonnes]) a.nouvelle(n);
  };
  s.onerror = () => {
    coupee = true;
    // Le navigateur réessaie seul tant que la connexion est CONNECTING ; fermée, il faut la rouvrir.
    if (s.readyState !== EventSource.CLOSED) return;
    s.close();
    if (source === s) source = null;
    window.clearTimeout(relance);
    relance = window.setTimeout(() => {
      if (abonnes.size) ouvrir();
    }, RELANCE);
  };
}

function fermer() {
  window.clearTimeout(relance);
  source?.close();
  source = null;
  coupee = false;
}

// S'abonne aux nouvelles ; rend la fonction qui désabonne.
export function ecouter(
  nouvelle: (n: Nouvelle) => void,
  reprise?: () => void,
): () => void {
  const a: Abonne = { nouvelle, reprise };
  abonnes.add(a);
  ouvrir();
  return () => {
    abonnes.delete(a);
    if (!abonnes.size) fermer();
  };
}
